import { useCallback, useEffect, useMemo, useState } from 'react';

import { absenceApprovalService } from '@/api/domains/absence/absenceApproval.service';
import type { BannerPart } from '@/components/ui/banners';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import type {
  AbsenceApprovalMonthlyCut,
  AbsenceCounterKey,
  AbsenceCutsSource,
} from '@/api/domains/absence/absenceApproval.types';


export interface AbsenceApprovalCutFiltersPatch {
  filterCompanyId?: number | null;
  monthlyCutId?: number | null;
  counterFilter?: AbsenceCounterKey;
}

export interface UseAbsenceApprovalMonthlyCutsParams {
  selectedCompanyValue: string;
  /** El padre fusiona esto con el snapshot de filtros avanzados pendiente y llama setFilters. */
  onFiltersPatch: (patch: AbsenceApprovalCutFiltersPatch) => void;
}

export interface UseAbsenceApprovalMonthlyCutsResult {
  isLoadingCuts: boolean;
  hasCuts: boolean;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  descriptionOptions: { value: string; label: string }[];
  selectedYear: number | null;
  selectedMonth: number | null;
  onYearChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  /** Segmentos del mensaje "sin cortes" con el énfasis resuelto por i18n. Null si no aplica. */
  noCutsMessageParts: BannerPart[] | null;
}

const ALL_COMPANIES_VALUE = 'all';

/**
 * Empresa + período (cortes mensuales) de Aprobación de Ausencias. Extraído de useAbsenceApproval
 * para no dejar ese hook monolítico — calcado de useOvertimeApprovalMonthlyCuts. No conoce los
 * filtros avanzados: en vez de escribir `filters` directamente, delega en `onFiltersPatch` (que el
 * padre fusiona con el snapshot avanzado pendiente).
 */
export function useAbsenceApprovalMonthlyCuts(
  params: UseAbsenceApprovalMonthlyCutsParams,
): UseAbsenceApprovalMonthlyCutsResult {
  const { selectedCompanyValue, onFiltersPatch } = params;

  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const [cuts, setCuts] = useState<AbsenceApprovalMonthlyCut[]>([]);
  const [cutsSource, setCutsSource] = useState<AbsenceCutsSource>('none');
  const [isLoadingCuts, setIsLoadingCuts] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  // Carga de cortes cada vez que cambia la empresa del filtro principal.
  useEffect(() => {
    if (!selectedCompanyValue) return;

    const controller = new AbortController();
    const isAllCompanies = selectedCompanyValue === ALL_COMPANIES_VALUE;
    const filterCompanyId = isAllCompanies ? null : Number(selectedCompanyValue);

    const loadCuts = async () => {
      setIsLoadingCuts(true);

      const response = await absenceApprovalService.getMonthlyCuts(
        filterCompanyId,
        controller.signal,
      );

      if (controller.signal.aborted) return;

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('absence:approval.errors.loadMonthlyCuts'));
        }
        setCuts([]);
        setCutsSource('none');
        setIsLoadingCuts(false);
        return;
      }

      const { monthlyCuts, defaultMonthlyCutId, cutsSource: source } = response.data;

      setCuts(monthlyCuts);
      setCutsSource(source);
      setIsLoadingCuts(false);

      const defaultCut = monthlyCuts.find((cut) => cut.monthlyCutId === defaultMonthlyCutId)
        ?? monthlyCuts[0]
        ?? null;

      setSelectedYear(defaultCut?.year ?? null);
      setSelectedMonth(defaultCut?.month ?? null);
      // Se aplican también los avanzados vigentes: al cambiar de empresa el panel ya sincronizó su
      // filtro de Empresa, y dejarlo fuera provocaría una segunda consulta al vencer el debounce.
      onFiltersPatch({
        filterCompanyId,
        monthlyCutId: defaultCut?.monthlyCutId ?? null,
        counterFilter: 'calculated',
      });
    };

    void loadCuts();

    return () => controller.abort();
  }, [selectedCompanyValue, showApiError, translate, onFiltersPatch]);

  const hasCuts = cutsSource !== 'none' && cuts.length > 0;

  const onYearChange = useCallback((value: string) => {
    const year = Number(value);
    const candidates = cuts.filter((cut) => cut.year === year);
    const cut = candidates.filter((candidate) => candidate.month === selectedMonth)[0]
      ?? candidates[0]
      ?? cuts[0]
      ?? null;

    setSelectedYear(cut?.year ?? null);
    setSelectedMonth(cut?.month ?? null);
    onFiltersPatch({ monthlyCutId: cut?.monthlyCutId ?? null });
  }, [cuts, selectedMonth, onFiltersPatch]);

  const onMonthChange = useCallback((value: string) => {
    const month = Number(value);
    const candidates = cuts.filter((cut) => cut.year === selectedYear && cut.month === month);
    const cut = candidates[0] ?? cuts[0] ?? null;

    setSelectedYear(cut?.year ?? null);
    setSelectedMonth(cut?.month ?? null);
    onFiltersPatch({ monthlyCutId: cut?.monthlyCutId ?? null });
  }, [cuts, selectedYear, onFiltersPatch]);

  const onDescriptionChange = useCallback((value: string) => {
    const monthlyCutId = Number(value);
    const cut = cuts.find((candidate) => candidate.monthlyCutId === monthlyCutId) ?? null;

    setSelectedYear(cut?.year ?? null);
    setSelectedMonth(cut?.month ?? null);
    onFiltersPatch({ monthlyCutId: cut?.monthlyCutId ?? null });
  }, [cuts, onFiltersPatch]);

  const yearOptions = useMemo(() => {
    const years = [...new Set(cuts.map((cut) => cut.year))].sort((a, b) => b - a);
    return years.map((year) => ({ value: String(year), label: String(year) }));
  }, [cuts]);

  const monthOptions = useMemo(() => {
    const monthsOfYear = cuts.filter((cut) => cut.year === selectedYear);
    const seen = new Map<number, string>();
    for (const cut of monthsOfYear) {
      if (!seen.has(cut.month)) seen.set(cut.month, cut.monthName);
    }
    return [...seen.entries()]
      .sort(([firstMonth], [secondMonth]) => secondMonth - firstMonth)
      .map(([month, monthName]) => ({ value: String(month), label: monthName }));
  }, [cuts, selectedYear]);

  const descriptionOptions = useMemo(() => cuts
    .filter((cut) => cut.year === selectedYear && cut.month === selectedMonth)
    .map((cut) => ({ value: String(cut.monthlyCutId), label: cut.description })),
  [cuts, selectedYear, selectedMonth]);

  const noCutsMessageParts = useMemo<BannerPart[] | null>(() => {
    if (isLoadingCuts || hasCuts || !selectedCompanyValue) return null;

    const keyPrefix = selectedCompanyValue === ALL_COMPANIES_VALUE
      ? 'absence:approval.messages.noHoldingCuts'
      : 'absence:approval.messages.noCompanyCuts';

    return [
      { text: translate(`${keyPrefix}Part1`), emphasis: false },
      { text: translate(`${keyPrefix}Part1Emphasis`), emphasis: true },
      { text: translate(`${keyPrefix}Part2`), emphasis: false },
      { text: translate(`${keyPrefix}Part2Emphasis`), emphasis: true },
      { text: translate(`${keyPrefix}Part3`), emphasis: false },
    ];
  }, [isLoadingCuts, hasCuts, selectedCompanyValue, translate]);

  return {
    isLoadingCuts,
    hasCuts,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    noCutsMessageParts,
  };
}

export default useAbsenceApprovalMonthlyCuts;
